/**
 * B31c (TRA-52) — departures for a picked card. The card carries the
 * product→schedule binding (contract-required since e75d760); this module
 * is the ONLY place the storefront reads slots for a product, and it reads
 * them by the card's scheduleIds — the productId never reaches slot.list.
 *
 * B31e (TRA-57) — availability reads are LAZY (the selected day only) and
 * BOUNDED (never more than AVAILABILITY_CONCURRENCY in flight). The eager
 * shape fired one slot.availability per ON_SALE slot in parallel — 270 for
 * dev-ferry — which exhausted the dev account's 10-slot Lambda concurrency
 * pool and turned every browser click-test into API GW 503s.
 */
import type { PoolAvailability, SlotSummary } from "../ui/screens.js";

export interface BrowseCard {
  readonly productId: string;
  readonly scheduleIds: readonly string[];
}

export interface BrowseApi {
  call(name: string, fields: Record<string, unknown>): Promise<unknown>;
}

export interface Departures {
  readonly slots: readonly SlotSummary[];
  /**
   * B355 leg (iv-a) — which schedule each slot came from. A union read over
   * many cards' schedules flattens away the one fact a per-card question
   * needs; keeping it is free at read time and impossible afterwards.
   */
  readonly bySchedule: Readonly<Record<string, readonly string[]>>;
}

export type AvailabilityMap = Readonly<Record<string, readonly PoolAvailability[]>>;

/**
 * Seats left on a slot: the MINIMUM available across its pools, because the
 * tightest pool is the wall — a sailing with 40 deck seats and 3 cabin berths
 * can sell 3 more mixed admissions, not 40.
 *
 * `undefined` when nothing has been read yet, and that distinction is the
 * whole point of the return type: an empty pool list means "not read", never
 * "sold out". A surface that collapsed the two would tell a seller a sailing
 * is full because a fetch had not landed.
 *
 * B355 — extracted from `SlotPicker`, which has computed exactly this inline
 * since B31. The POS sailing strip needs the same number, and a till free to
 * re-derive it is a till free to disagree with the buyer's own screen about
 * whether a departure is full. B230/B231(d)/B245's stance: a copy that cannot
 * exist cannot drift.
 */
export function seatsLeft(pools: readonly PoolAvailability[]): number | undefined {
  if (pools.length === 0) return undefined;
  return pools.reduce((min, p) => Math.min(min, p.available), Number.POSITIVE_INFINITY);
}

/** An unbound card ([]) has no departure step at all (non-slotted product). */
export function needsDepartureStep(card: BrowseCard): boolean {
  return card.scheduleIds.length > 0;
}

/**
 * Slot lists for the card's bound schedules — no availability reads here.
 *
 * B355 leg (iv-a) — the read also KEEPS which schedule answered, because a
 * union read otherwise throws that away. The POS reads the union of every
 * card's schedules once (leg (i)) and then has to answer, per tile, "is the
 * sailing this sale picked one of THIS card's?" — a question the flattened
 * list cannot answer. Recording it here costs nothing (the loop already
 * knows) and is the alternative to a second read per product at the counter.
 */
export async function loadDepartures(api: BrowseApi, card: BrowseCard): Promise<Departures> {
  const slots: SlotSummary[] = [];
  const bySchedule: Record<string, readonly string[]> = {};
  for (const scheduleId of card.scheduleIds) {
    const res = (await api.call("slot.list", { scheduleId })) as { slots: readonly SlotSummary[] };
    slots.push(...res.slots);
    bySchedule[scheduleId] = res.slots.map((s) => s.slotId);
  }
  slots.sort((a, b) => a.start - b.start);
  return { slots, bySchedule };
}

/**
 * The ON_SALE occurrences a card is actually sold at, out of a UNION read.
 *
 * B355 leg (iv-a) — shared for the reason `seatsLeft` is shared: the till's
 * tile grid and the buyer's departure step are answering the same question
 * about the same card, and two derivations of "which sailings is this sold
 * at" are two ways to sell a ticket for a boat the product does not run on.
 *
 * A card whose schedules are absent from the map answers [] — honest
 * absence, the `seatsLeft` rule again: not-read-yet is never "none on sale"
 * silently, it is a shut door the screen must SAY something about.
 */
export function cardSellableSlotIds(
  card: { readonly scheduleIds: readonly string[] },
  union: Departures,
): readonly string[] {
  const mine = new Set<string>();
  for (const scheduleId of card.scheduleIds) for (const slotId of union.bySchedule[scheduleId] ?? []) mine.add(slotId);
  return union.slots.filter((s) => s.status === "ON_SALE" && mine.has(s.slotId)).map((s) => s.slotId);
}

export const AVAILABILITY_CONCURRENCY = 4;

/**
 * Availability for exactly the given slots, at most AVAILABILITY_CONCURRENCY
 * requests in flight. A refused read is OMITTED — the picker renders unknown
 * (no seat chip), never a lying zero; cart.add stays the real wall (Inv 2/3).
 */
export async function loadAvailability(
  api: BrowseApi,
  slotIds: readonly string[],
  limit: number = AVAILABILITY_CONCURRENCY,
): Promise<AvailabilityMap> {
  const out: Record<string, readonly PoolAvailability[]> = {};
  const queue = [...slotIds];
  const worker = async (): Promise<void> => {
    for (let slotId = queue.shift(); slotId !== undefined; slotId = queue.shift()) {
      try {
        const a = (await api.call("slot.availability", { slotId })) as { pools: readonly PoolAvailability[] };
        out[slotId] = a.pools;
      } catch {
        // unknown stays unknown — never a fabricated availability
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(0, Math.min(limit, queue.length)) }, worker));
  return out;
}

/* ---------------------------------------------------------- B31h(a) fit */

export interface FitDepartures {
  readonly slots: readonly SlotSummary[];
  /** slotId → the server's §2.4 verdict. Empty = no verdict (degraded). */
  readonly fits: Readonly<Record<string, boolean>>;
  /** Availability delivered by the SAME read — one round trip per calendar. */
  readonly availability: AvailabilityMap;
}

interface FitSlotWire {
  readonly slotId: string;
  readonly start: number;
  readonly end: number;
  readonly status: SlotSummary["status"];
  readonly fits?: boolean;
  readonly pools: readonly PoolAvailability[];
}

/**
 * B31h(a) (RULING 2026-07-24 #1) — the TICKETS_FIRST exact-fit calendar.
 * ONE slot.fit per bound schedule carries the composed party (zero-qty
 * entries stripped); the SERVER computes fit through the §2.4/§6 matrix and
 * answers verdict + availability together — the matrix never reaches the
 * browser, and the calendar paints in one round trip. A refused read (older
 * stage, transient refusal) degrades honestly to loadDepartures: no verdict,
 * seat chips as before, cart.add stays the real wall (Inv 2/3). Journeys:
 * the response already carries only BOARDING-leg candidates, verdicted
 * across every leg of the O-D.
 */
export async function loadFitDepartures(
  api: BrowseApi,
  card: BrowseCard,
  channelId: string,
  composition: Readonly<Record<string, number>>,
): Promise<FitDepartures> {
  const party: Record<string, number> = {};
  for (const [variantId, qty] of Object.entries(composition)) if (qty > 0) party[variantId] = qty;
  if (Object.keys(party).length === 0) {
    return { ...(await loadDepartures(api, card)), fits: {}, availability: {} };
  }
  const slots: SlotSummary[] = [];
  const fits: Record<string, boolean> = {};
  const availability: Record<string, readonly PoolAvailability[]> = {};
  try {
    for (const scheduleId of card.scheduleIds) {
      const res = (await api.call("slot.fit", { scheduleId, productId: card.productId, channelId, party })) as {
        slots: readonly FitSlotWire[];
      };
      for (const s of res.slots) {
        slots.push({ slotId: s.slotId, start: s.start, end: s.end, status: s.status });
        if (s.fits !== undefined) fits[s.slotId] = s.fits;
        availability[s.slotId] = s.pools;
      }
    }
  } catch {
    return { ...(await loadDepartures(api, card)), fits: {}, availability: {} };
  }
  slots.sort((a, b) => a.start - b.start);
  return { slots, fits, availability };
}

/* ------------------------------------------------------------- B498 race */

/** What one departure-sheet read delivers; fits/availability only from the
 * fit read (B31h(a)) — a plain read leaves them absent, not empty. */
export interface DeparturesAnswer {
  readonly slots: readonly SlotSummary[];
  readonly fits?: Readonly<Record<string, boolean>>;
  readonly availability?: AvailabilityMap;
}

/**
 * B498 — the departure sheet loads NEWEST-WINS (the B492–B497 late-settle
 * class, storefront flavor). `openDepartures` and `openFitDepartures`
 * write the same surface (`slots`/`fits`/the spinner) with no gate, so a
 * buyer who opens card A's sheet, backs out, and opens card B's gets A's
 * departures landing LAST under B's sheet — tell-free, `slots` records no
 * card — and A's `finally` clears the spinner while B's read still flies.
 * One opener per surface: `begin` always runs (opening a sheet always
 * wipes the previous card's rows), but only the NEWEST open's answer or
 * refusal writes, and only the newest settles the spinner. Extracted here
 * so the drive is a unit test, not a browser walk.
 */
export function createDeparturesOpener(hooks: {
  readonly begin: () => void;
  readonly answer: (d: DeparturesAnswer) => void;
  readonly refuse: (error: unknown) => void;
  readonly settle: () => void;
}): (work: () => Promise<DeparturesAnswer>) => Promise<void> {
  let gen = 0;
  return async (work) => {
    const mine = ++gen;
    const live = (): boolean => mine === gen; // a newer open owns the sheet
    hooks.begin();
    try {
      const d = await work();
      if (live()) hooks.answer(d);
    } catch (error) {
      if (live()) hooks.refuse(error);
    } finally {
      if (live()) hooks.settle();
    }
  };
}

/* ── B521 open leg 2 — the calendar's per-day prices ──────────────────── */

import type { CalendarPricingWire } from "../ui/departures.js";

/**
 * The product.calendar answer for a card, or undefined on refusal — the
 * picker then draws NO figures (honest absence; the tickets step and the
 * cart stay the priced truth).
 */
export async function loadCalendarPricing(
  api: BrowseApi,
  card: { readonly productId: string },
  channelId: string,
): Promise<CalendarPricingWire | undefined> {
  try {
    const res = (await api.call("product.calendar", { productId: card.productId, channelId })) as {
      pricing?: CalendarPricingWire;
    };
    return res.pricing;
  } catch {
    return undefined;
  }
}
